'use client';

import { TRACKING_PARAM_KEYS } from '@/constants/tracking-params';
import { usePathname, useSearchParams } from 'next/navigation';
import LinkPagination from '../link-pagination';

// 分页段只认路径末尾的 `/p/{数字}`。
//
// 原先用 `findIndex(x => x === 'p')` 找第一个 `p` 段,遇到
// `/wedding-guest-dresses/p/6/internal-sort/best_sellers` 这类历史畸形路径时会误判,
// 再叠加下面的定位插入逻辑,就会派生出 `/p/privacy-policy/4` 这种不存在的地址。
// 应用自身生成的分页链接一律是 `.../p/N` 结尾,所以只匹配末尾是安全的。
const TRAILING_PAGINATION = /\/p\/\d+$/;

const CategoryPagination = props => {
  const { currentPage, totalPages } = props;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const pathParts = pathname.split('/').filter(Boolean);

  const hasTrailingPagination = TRAILING_PAGINATION.test(pathname);
  // 路径里有 `p` 段却不是合法的末尾分页 —— 说明这个 URL 本身是畸形的
  // (旧链接、外部拼错、或历史 bug 产出)。此时不生成任何分页链接,
  // 避免以错误路径为基础继续派生出新的坏 URL。
  const hasStrayPaginationKey = !hasTrailingPagination && pathParts.includes('p');

  const generatePaginationHref = nextPage => {
    const nextPathParts = pathParts.slice(0);

    if (hasTrailingPagination) {
      // 去掉末尾的 `p` 和页码,后面按需重新追加
      nextPathParts.splice(nextPathParts.length - 2, 2);
    }

    if (nextPage > 1) {
      nextPathParts.push('p', String(nextPage));
    }

    const nextSearchParams = new URLSearchParams(searchParams.toString());

    nextSearchParams.delete('sku');
    nextSearchParams.delete('fcolor');

    // 追踪参数不能透传到分页链接。否则爬虫顺着分页往下走,
    // 每一页都会派生出一个带参数的 URL 变体,在 GSC 里堆成成百上千条重复页。
    for (const key of Array.from(nextSearchParams.keys())) {
      if (TRACKING_PARAM_KEYS.has(key)) {
        nextSearchParams.delete(key);
      }
    }

    const searchParamStr = nextSearchParams.toString();

    return `/${nextPathParts.join('/')}` + (searchParamStr ? `?${searchParamStr}` : '');
  };

  const numericTotalPages = typeof totalPages === 'number' ? totalPages : parseInt(totalPages);
  const numericCurrentPage = typeof currentPage === 'number' ? currentPage : parseInt(currentPage);

  if (!numericTotalPages || numericTotalPages <= 1 || hasStrayPaginationKey) {
    return null;
  }

  return (
    <LinkPagination
      currentPage={numericCurrentPage}
      totalPages={numericTotalPages}
      generatePaginationHref={generatePaginationHref}
      className='mt-[30px] lg_mt-[54px]'
    />
  );
};

export default CategoryPagination;
